import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { toast } from 'react-toastify';
import useAdd from '../api/useAdd';

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isValidToken, setIsValidToken] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [resetComplete, setResetComplete] = useState(false);
  const { handleAdd, loading, success } = useAdd('users/reset-password-confirm');

  useEffect(() => {
    const validateToken = async () => {
      try {
        // Since we don't have a specific endpoint for token validation,
        // we'll just check if the token exists
        if (token) {
        setIsValidToken(true);
        } else {
          setIsValidToken(false);
        }
      } catch (error) {
        toast.error(t('reset_password.invalid_token'));
        setIsValidToken(false);
      } finally {
        setIsLoading(false);
      }
    };

    validateToken();
  }, [token, t]);

  const initialValues = {
    password: '',
    confirmPassword: '',
  };

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .min(8, t('validation.password_length'))
      .required(t('validation.required_field')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], t('validation.passwords_must_match'))
      .required(t('validation.required_field')),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await handleAdd({
        token,
        password: values.password,
        confirmPassword: values.confirmPassword
      });
      setResetComplete(true);
      toast.success(t('reset_password.success_message'));
    } catch (error) {
      toast.error(
        error.response?.data?.message || t('common.something_went_wrong')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  if (isLoading) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
          }}
        >
          <Typography>{t('common.loading')}</Typography>
        </Box>
      </Container>
    );
  }

  if (!isValidToken) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 8,
          }}
        >
          <Typography component="h1" variant="h4" gutterBottom color="error">
            {t('reset_password.invalid_link_title')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('reset_password.invalid_link_message')}
          </Typography>
          <Button
            variant="contained"
            component={RouterLink}
            to="/forgot-password"
          >
            {t('reset_password.try_again')}
          </Button>
        </Box>
      </Container>
    );
  }

  if (resetComplete) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 8,
          }}
        >
          <Typography component="h1" variant="h4" gutterBottom>
            {t('reset_password.reset_successful')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('reset_password.reset_successful_message')}
          </Typography>
          <Button variant="contained" component={RouterLink} to="/login">
            {t('reset_password.back_to_login')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 8,
        }}
      >
        <Typography component="h1" variant="h4" gutterBottom>
          {t('reset_password.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={4}>
          {t('reset_password.subtitle')}
        </Typography>

        <Card sx={{ width: '100%' }}>
          <CardContent>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({
                errors,
                touched,
                isSubmitting,
                values,
                handleChange,
                handleBlur,
              }) => (
                <Form>
                  <Stack spacing={3}>
                    <FormControl
                      fullWidth
                      variant="outlined"
                      error={touched.password && Boolean(errors.password)}
                    >
                      <InputLabel>{t('reset_password.new_password')}</InputLabel>
                      <OutlinedInput
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleTogglePassword}
                              edge="end"
                              size="large"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        }
                        label={t('reset_password.new_password')}
                      />
                      {touched.password && errors.password && (
                        <FormHelperText>{errors.password}</FormHelperText>
                      )}
                    </FormControl>

                    <FormControl
                      fullWidth
                      variant="outlined"
                      error={
                        touched.confirmPassword &&
                        Boolean(errors.confirmPassword)
                      }
                    >
                      <InputLabel>
                        {t('reset_password.confirm_password')}
                      </InputLabel>
                      <OutlinedInput
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={values.confirmPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleToggleConfirmPassword}
                              edge="end"
                              size="large"
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        }
                        label={t('reset_password.confirm_password')}
                      />
                      {touched.confirmPassword && errors.confirmPassword && (
                        <FormHelperText>
                          {errors.confirmPassword}
                        </FormHelperText>
                      )}
                    </FormControl>

                    <Button
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                    >
                      {t('reset_password.reset_button')}
                    </Button>
                  </Stack>
                </Form>
              )}
            </Formik>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ResetPassword; 